/**
 * @file WCHUpgrader.ts
 * @description 沁恒 (WCH) 固件升级器
 *
 * 基于私有 HID Output Report 协议进行升级。
 * 流程: 擦除Flash → 写入固件(0-50%) → 验证固件(50-100%) → 结束
 *
 * 命令协议:
 *   - CMD_IAP_ERASE  (0x81): 擦除Flash
 *   - CMD_IAP_PROM   (0x80): 写入固件数据块
 *   - CMD_IAP_VERIFY (0x82): 验证固件数据块
 *   - CMD_IAP_END    (0x83): 结束升级
 *
 * 通信方式:
 *   - HID Output Report ID: 0x00
 *   - 发送缓冲区大小: 512 字节
 *   - 数据块大小: 32 字节 (0x20)
 */

import { BaseDevice } from '../../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'

// 命令常量
const CMD_IAP_ERASE = 0x81
const CMD_IAP_PROM = 0x80
const CMD_IAP_VERIFY = 0x82
const CMD_IAP_END = 0x83

const REPORT_ID = 0x00
const DATA_MAX_LENGTH = 512
const CHUNK_SIZE = 0x20 // 32 bytes per write/verify chunk

export class WCHUpgrader {
  private device: BaseDevice
  private firmwareData: Uint8Array = new Uint8Array(0)

  private onProgressCallback: ((progress: number) => void) | null = null
  private onProgressFinishCallback: (() => void) | null = null

  constructor(device: BaseDevice) {
    this.device = device
  }

  // ==================== 公共方法 ====================

  setOnProgressCallback(callback: (progress: number) => void) {
    this.onProgressCallback = callback
  }

  setOnProgressFinishCallback(callback: () => void) {
    this.onProgressFinishCallback = callback
  }

  /**
   * 发送 Output Report 进入更新模式（让设备切到 Bootloader）
   *
   * WCH 沁恒升级：鼠标本体走 [0xff, 0xa8]，接收器走 [0xb1]，
   * 由调用方按场景决定 payload，本方法只负责透传。
   *
   * @param payload 进入更新模式的命令字节，默认 [0xff, 0xa8]
   */
  async enterUpdateMode(payload: number[] = [0xff, 0xa8]): Promise<void> {
    const sendData = new Uint8Array(payload)
    return this.sendReport(sendData)
  }

  /**
   * 启动 WCH 固件升级
   * @param firmwareData .bin 固件数据
   */
  async startUpdate(firmwareData: Uint8Array): Promise<void> {
    this.firmwareData = firmwareData

    try {
      // 1. 擦除 Flash
      console.log('[WCH] 擦除设备 Flash...')
      await this.eraseDeviceFlash()

      // 2. 写入固件 (进度 0% → 50%)
      console.log('[WCH] 开始写入固件...')
      await this.writeDeviceFlashData()

      // 3. 验证固件 (进度 50% → 100%)
      console.log('[WCH] 验证固件...')
      const isVerified = await this.checkDeviceFlashData()
      if (!isVerified) {
        throw new DeviceError(DeviceErrorCode.WRITE_FAILED, 'WCH 固件验证失败')
      }

      console.log('[WCH] 固件升级完成')
    } catch (error) {
      console.error('[WCH] 固件升级失败:', error)
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `WCH 升级失败: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    }
  }

  /**
   * 结束设备更新 (重启)
   */
  async endDeviceUpdate(): Promise<void> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    sendBuf[0] = CMD_IAP_END
    sendBuf[1] = 0x02

    console.log('[WCH] 发送结束升级命令...')
    await this.sendReport(sendBuf)
  }

  // ==================== 私有方法 ====================

  /**
   * 通过 Output Report 发送数据到设备
   */
  private async sendReport(data: Uint8Array): Promise<void> {
    const buf = new Uint8Array(data)
    await this.device.channel.sendOutputReport(REPORT_ID, buf)
  }

  /**
   * 发送设备数据 (带 5ms 等待)
   */
  private async sendDeviceData(data: Uint8Array): Promise<void> {
    const writeBuf = new Uint8Array(DATA_MAX_LENGTH)
    if (data) {
      const copyLen = Math.min(data.length, DATA_MAX_LENGTH)
      writeBuf.set(data.subarray(0, copyLen))
    }
    await this.sendReport(writeBuf)
    await this.msleep(5)
  }

  private msleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms))
  }

  private emitSendUpdateProgress(percent: number) {
    const p = Math.min(100, Math.max(0, Math.floor(percent * 100) / 100))
    if (this.onProgressCallback) {
      this.onProgressCallback(p)
      if (p === 100) {
        this.emitUpdateFinished()
      }
    }
  }

  private emitUpdateFinished() {
    if (this.onProgressFinishCallback) {
      this.onProgressFinishCallback()
    }
  }

  /**
   * 擦除设备 Flash
   */
  private async eraseDeviceFlash(): Promise<void> {
    const data = new Uint8Array(DATA_MAX_LENGTH)
    data[0] = CMD_IAP_ERASE
    data[1] = 0x20
    await this.sendReport(data)
  }

  /**
   * 写入固件数据到设备 (进度 0% → 50%)
   */
  private async writeDeviceFlashData(): Promise<boolean> {
    if (!this.firmwareData || this.firmwareData.length === 0) return false

    let dataLength = 0
    const chunkSize = CHUNK_SIZE
    const totalChunks = Math.ceil(this.firmwareData.length / chunkSize)

    for (let i = 0; i < totalChunks; i++) {
      const offset = i * chunkSize
      const writeLength =
        offset + chunkSize > this.firmwareData.length
          ? this.firmwareData.length - offset
          : chunkSize

      const chunk = this.firmwareData.subarray(offset, offset + writeLength)

      // 构建写入命令: [CMD, len, offset_low, offset_high, ...data]
      const cmd = new Uint8Array(4 + chunk.length)
      cmd[0] = CMD_IAP_PROM
      cmd[1] = chunk.length
      cmd[2] = dataLength & 0xff
      cmd[3] = (dataLength >> 8) & 0xff
      cmd.set(chunk, 4)

      await this.sendDeviceData(cmd)
      dataLength += writeLength

      // 更新进度: 0% → 50%
      const progress = Math.round(((i + 1) / totalChunks) * 50)
      this.emitSendUpdateProgress(progress)
    }

    return true
  }

  /**
   * 验证固件数据 (进度 50% → 100%)
   */
  private async checkDeviceFlashData(): Promise<boolean> {
    if (!this.firmwareData || this.firmwareData.length === 0) return false

    let dataLength = 0
    const fileSize = this.firmwareData.length

    while (dataLength < fileSize) {
      const writeLength = Math.min(CHUNK_SIZE, fileSize - dataLength)

      const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
      sendBuf[0] = CMD_IAP_VERIFY
      sendBuf[1] = writeLength
      sendBuf[2] = dataLength & 0xff
      sendBuf[3] = (dataLength >> 8) & 0xff

      // 复制数据块 (偏移5开始)
      sendBuf.set(this.firmwareData.subarray(dataLength, dataLength + writeLength), 5)

      await this.sendDeviceData(sendBuf)
      dataLength += writeLength

      // 更新进度: 50% → 100%
      const progress = 50 + Math.round((dataLength * 50) / fileSize)
      this.emitSendUpdateProgress(progress)
    }

    return true
  }
}
